import {
  EMPTY_SLIDE_LAYOUT,
  parseSlideLayoutJson,
  type ProgramSlideLayoutV1,
} from './programContentSlideCanvas';
import { formatWebDocumentAcademicYearLabel } from './evaluationPlanWebDocument';

export const EVALUATION_PLAN_SHEET_CANVAS_VERSION = 1 as const;
export const EVALUATION_PLAN_SHEET_CANVAS_TITLE = '평가지' as const;
/** 헤더 밴드 초기·폴백 높이(실제는 캔버스에서 콘텐츠 높이로 측정) */
export const EVALUATION_PLAN_SHEET_HEADER_BAND_NORM = 0.1 as const;
/** 학년도 텍스트 아래 편집 가능 영역까지 최소 여백(px) */
export const EVALUATION_PLAN_SHEET_HEADER_BAND_CONTENT_GAP_PX = 6 as const;
export const EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX = 18 as const;
export const EVALUATION_PLAN_SHEET_DEFAULT_POLYLINE_STROKE_WIDTH_PX = 4 as const;
/** body portal 미니툴바·표 편집 — 평가지 편집 모달(10340) 위, 표 생성 모달(10350) 아래 */
export const EVALUATION_PLAN_SHEET_FLOATING_UI_Z = 10345 as const;

export type EvaluationPlanSheetImageScope = 'admin' | 'personal';

export type ProgramSlideLayoutSubsetV1 = Pick<
  ProgramSlideLayoutV1,
  | 'boxes'
  | 'shapes'
  | 'polylines'
  | 'tables'
  | 'images'
  | 'clipboardImages'
  | 'scanImages'
  | 'webpages'
  | 'defaultTextColorHex'
  | 'defaultBackgroundColorHex'
  | 'pageBackgroundColorHex'
> & {
  v: 1;
};

export type EvaluationPlanSheetCanvasDocumentV1 = {
  version: typeof EVALUATION_PLAN_SHEET_CANVAS_VERSION;
  slot: 'sheet';
  title: string;
  titleReadOnly: true;
  headerBandNorm: number;
  academicYearLabel: string;
  /** 1쪽(머리말 띠 포함). 예전 단일 페이지 문서와 동일 필드 → 하위 호환 */
  layout: ProgramSlideLayoutSubsetV1;
  /**
   * 2쪽 이후(선택). 없으면 1쪽 문서. 버전은 1 유지(추가 필드) — 예전 클라이언트는 1쪽만 표시.
   * 2쪽 이후에는 머리말 띠 없음(상단 여백만).
   */
  extraPages?: ProgramSlideLayoutSubsetV1[];
};

/** 평가지 2쪽 이후 상단 여백(정규화) */
export const EVALUATION_PLAN_SHEET_CONTINUATION_TOP_NORM = 0.04 as const;
export const EVALUATION_PLAN_SHEET_MAX_PAGES = 10 as const;

/** 모든 페이지 [1쪽, 2쪽, …] */
export function getSheetCanvasPages(doc: EvaluationPlanSheetCanvasDocumentV1): ProgramSlideLayoutSubsetV1[] {
  return [doc.layout, ...(doc.extraPages ?? [])];
}

/** 페이지 배열로 문서 갱신 (첫 페이지 → layout, 나머지 → extraPages; 1쪽이면 extraPages 생략) */
export function withSheetCanvasPages(
  doc: EvaluationPlanSheetCanvasDocumentV1,
  pages: ProgramSlideLayoutSubsetV1[]
): EvaluationPlanSheetCanvasDocumentV1 {
  const [first, ...rest] = pages.length ? pages : [createEmptySheetLayoutSubset()];
  const next: EvaluationPlanSheetCanvasDocumentV1 = { ...doc, layout: first };
  if (rest.length) next.extraPages = rest;
  else delete next.extraPages;
  return next;
}

export function createEmptySheetLayoutSubset(): ProgramSlideLayoutSubsetV1 {
  return {
    v: 1,
    boxes: [],
    shapes: [],
    polylines: [],
    tables: [],
    images: [],
    clipboardImages: [],
    scanImages: [],
    webpages: [],
    pageBackgroundColorHex: '#ffffff',
    defaultTextColorHex: '#111111',
  };
}

export function sheetLayoutSubsetToSlideLayout(
  subset: ProgramSlideLayoutSubsetV1
): ProgramSlideLayoutV1 {
  return {
    ...EMPTY_SLIDE_LAYOUT,
    v: 1,
    defaultTextColorHex: subset.defaultTextColorHex,
    defaultBackgroundColorHex: subset.defaultBackgroundColorHex,
    pageBackgroundColorHex: subset.pageBackgroundColorHex ?? '#ffffff',
    boxes: [...(subset.boxes ?? [])],
    shapes: [...(subset.shapes ?? [])],
    polylines: [...(subset.polylines ?? [])],
    tables: [...(subset.tables ?? [])],
    images: [...(subset.images ?? [])],
    clipboardImages: [...(subset.clipboardImages ?? [])],
    scanImages: [...(subset.scanImages ?? [])],
    webpages: [...(subset.webpages ?? [])],
  };
}

export function slideLayoutToSheetLayoutSubset(layout: ProgramSlideLayoutV1): ProgramSlideLayoutSubsetV1 {
  return {
    v: 1,
    defaultTextColorHex: layout.defaultTextColorHex,
    defaultBackgroundColorHex: layout.defaultBackgroundColorHex,
    pageBackgroundColorHex: layout.pageBackgroundColorHex,
    boxes: [...(layout.boxes ?? [])],
    shapes: [...(layout.shapes ?? [])],
    polylines: [...(layout.polylines ?? [])],
    tables: [...(layout.tables ?? [])],
    images: [...(layout.images ?? [])],
    clipboardImages: [...(layout.clipboardImages ?? [])],
    scanImages: [...(layout.scanImages ?? [])],
    /** 평가지에서는 소스코드 블럭(mode 'code')만 사용 */
    webpages: (layout.webpages ?? []).filter((w) => w.mode === 'code'),
  };
}

export function createDefaultEvaluationPlanSheetCanvasDocument(): EvaluationPlanSheetCanvasDocumentV1 {
  return {
    version: EVALUATION_PLAN_SHEET_CANVAS_VERSION,
    slot: 'sheet',
    title: EVALUATION_PLAN_SHEET_CANVAS_TITLE,
    titleReadOnly: true,
    headerBandNorm: EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
    academicYearLabel: formatWebDocumentAcademicYearLabel(),
    layout: createEmptySheetLayoutSubset(),
  };
}

function parseSheetLayoutSubset(raw: unknown): ProgramSlideLayoutSubsetV1 {
  const parsed = parseSlideLayoutJson(raw);
  return slideLayoutToSheetLayoutSubset(parsed);
}

export function parseEvaluationPlanSheetCanvasDocument(raw: unknown): EvaluationPlanSheetCanvasDocumentV1 | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Partial<EvaluationPlanSheetCanvasDocumentV1>;
  if (obj.version !== EVALUATION_PLAN_SHEET_CANVAS_VERSION) return null;
  if (obj.slot !== 'sheet') return null;

  const headerBandNorm =
    typeof obj.headerBandNorm === 'number' && Number.isFinite(obj.headerBandNorm)
      ? Math.min(0.35, Math.max(0.08, obj.headerBandNorm))
      : EVALUATION_PLAN_SHEET_HEADER_BAND_NORM;

  return {
    version: EVALUATION_PLAN_SHEET_CANVAS_VERSION,
    slot: 'sheet',
    title:
      typeof obj.title === 'string' && obj.title.trim()
        ? obj.title.trim()
        : EVALUATION_PLAN_SHEET_CANVAS_TITLE,
    titleReadOnly: true,
    headerBandNorm,
    academicYearLabel:
      typeof obj.academicYearLabel === 'string' && obj.academicYearLabel.trim()
        ? obj.academicYearLabel.trim()
        : formatWebDocumentAcademicYearLabel(),
    layout: parseSheetLayoutSubset(obj.layout),
    ...(Array.isArray(obj.extraPages) && obj.extraPages.length
      ? { extraPages: obj.extraPages.slice(0, EVALUATION_PLAN_SHEET_MAX_PAGES - 1).map((p) => parseSheetLayoutSubset(p)) }
      : {}),
  };
}

export function cloneEvaluationPlanSheetCanvasDocument(
  data: EvaluationPlanSheetCanvasDocumentV1
): EvaluationPlanSheetCanvasDocumentV1 {
  return parseEvaluationPlanSheetCanvasDocument(data) ?? createDefaultEvaluationPlanSheetCanvasDocument();
}

export function sheetCanvasDocumentHasContent(data: EvaluationPlanSheetCanvasDocumentV1): boolean {
  return getSheetCanvasPages(data).some((layout) => sheetLayoutSubsetHasContent(layout));
}

function sheetLayoutSubsetHasContent(layout: ProgramSlideLayoutSubsetV1): boolean {
  if ((layout.boxes ?? []).some((b) => (b.text ?? '').trim())) return true;
  if ((layout.images ?? []).some((im) => (im.src ?? '').trim())) return true;
  if ((layout.clipboardImages ?? []).some((im) => (im.src ?? '').trim())) return true;
  if ((layout.scanImages ?? []).some((im) => (im.src ?? '').trim())) return true;
  if ((layout.shapes ?? []).some((sh) => (sh.text ?? '').trim() || (sh.fillColorHex ?? '').trim())) return true;
  if ((layout.polylines ?? []).some((pl) => (pl.points ?? []).length >= 2)) return true;
  if ((layout.tables ?? []).some((tbl) => (tbl.cells ?? []).some((c) => (c.text ?? '').trim()))) return true;
  if ((layout.webpages ?? []).some((w) => (w.sourceCode ?? '').trim())) return true;
  return false;
}
